import React, { useState, useEffect } from 'react';
import './DualProtection.scss';

const DualProtection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState('connected');

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const animationCycle = setInterval(() => {
      setActiveAnimation(prev => {
        if (prev === 'connected') return 'disconnected';
        if (prev === 'disconnected') return 'activated';
        return 'connected';
      });
    }, 6000);

    return () => clearInterval(animationCycle);
  }, []);

  return (
    <section className={`dual-protection ${isVisible ? 'visible' : ''}`}>
      <div className="floating-particles">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`particle particle-${i + 1}`}></div>
        ))}
      </div>

      <div className="container">
        <div className="section-header">
          <div className="header-badge">
            <span className="badge-pulse"></span>
            DUAL PROTECTION SYSTEM
          </div>
          <h2>Ultimate Vehicle Security</h2>
          <p>Advanced Bluetooth-connected dual tracker system for maximum protection</p>
        </div>

        <div className="protection-showcase">
          <div className="showcase-glow"></div>
          
          <div className="devices-visualization">
            <div className="device-container starlink-container">
              <div className="device-frame">
                <div className="device-image">
                  <img src="/images/starlink-tracker-new.png" alt="Starlink Device" />
                </div>
                <div className="device-label">
                  <div className="device-name">Starlink Tracker</div>
                  <div className="device-type">Visible • Fleet Management</div>
                </div>
                <div className="device-status">
                  <div className={`status-indicator ${activeAnimation === 'disconnected' || activeAnimation === 'activated' ? 'offline' : 'online'}`}>
                    <div className="status-dot"></div>
                    <span>{activeAnimation === 'disconnected' || activeAnimation === 'activated' ? 'REMOVED' : 'ACTIVE'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bluetooth-connection">
              <div className={`connection-visualization ${activeAnimation}`}>
                {activeAnimation === 'connected' && (
                  <div className="bluetooth-waves">
                    <div className="bluetooth-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                        <path d="M17.71 7.71L12 2H11V9.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L11 14.41V22H12L17.71 16.29L13.41 12L17.71 7.71ZM13 5.83L15.17 8L13 10.17V5.83ZM13 13.83L15.17 16L13 18.17V13.83Z" fill="#00d4ff"/>
                      </svg>
                    </div>
                    <div className="signal-rings">
                      <div className="signal-ring ring-1"></div>
                      <div className="signal-ring ring-2"></div>
                      <div className="signal-ring ring-3"></div>
                    </div>
                    <div className="connection-beam"></div>
                  </div>
                )}
                
                {activeAnimation === 'disconnected' && (
                  <div className="disconnection-effect">
                    <div className="disconnect-icon">
                      <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="#ff4444"/>
                      </svg>
                    </div>
                    <div className="disconnect-text">CONNECTION LOST</div>
                    <div className="warning-pulse"></div>
                  </div>
                )}

                {activeAnimation === 'activated' && (
                  <div className="activation-effect">
                    <div className="activation-icon">
                      <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L15.09 8.26L22 9L15.09 9.74L12 16L8.91 9.74L2 9L8.91 8.26L12 2Z" fill="#00ff88"/>
                      </svg>
                    </div>
                    <div className="activation-text">SHADOW ACTIVATED</div>
                    <div className="success-pulse"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="device-container shadow-container">
              <div className="device-frame">
                <div className="device-image">
                  <img src="/images/iot-link-shadow.webp" alt="Shadow Device" />
                </div>
                <div className="device-label">
                  <div className="device-name">Shadow Tracker</div>
                  <div className="device-type">Hidden • Backup Protection</div>
                </div>
                <div className="device-status">
                  <div className={`status-indicator ${activeAnimation === 'activated' ? 'online' : 'standby'}`}>
                    <div className="status-dot"></div>
                    <span>{activeAnimation === 'activated' ? 'TRACKING' : 'STANDBY'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="protection-flow">
            <div className={`flow-step step-1 ${activeAnimation === 'connected' ? 'active' : ''}`}>
              <div className="step-icon">🔗</div>
              <div className="step-content">
                <h4>Bluetooth Connected</h4>
                <p>Devices communicate seamlessly via Bluetooth Low Energy</p>
              </div>
            </div>

            <div className={`flow-step step-2 ${activeAnimation === 'disconnected' ? 'active' : ''}`}>
              <div className="step-icon">⚠️</div>
              <div className="step-content">
                <h4>Connection Lost</h4>
                <p>Starlink removal triggers automatic Shadow activation</p>
              </div>
            </div>

            <div className={`flow-step step-3 ${activeAnimation === 'activated' ? 'active' : ''}`}>
              <div className="step-icon">🛡️</div>
              <div className="step-content">
                <h4>Backup Engaged</h4>
                <p>Shadow begins live tracking and sends theft alerts</p>
              </div>
            </div>
          </div>
        </div>

        <div className="protection-benefits">
          <div className="benefit-card">
            <div className="benefit-icon">🎯</div>
            <h4>Decoy Strategy</h4>
            <p>Starlink acts as the obvious target while Shadow remains completely hidden</p>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">⚡</div>
            <h4>Instant Activation</h4>
            <p>Bluetooth disconnection triggers immediate Shadow tracker activation</p>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">🔄</div>
            <h4>Seamless Backup</h4>
            <p>Automatic failover ensures continuous protection without gaps</p>
          </div>
          
          <div className="benefit-card">
            <div className="benefit-icon">📊</div>
            <h4>Dual Intelligence</h4>
            <p>Fleet management and theft recovery combined in one system</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DualProtection;
